/**
 * Drawdown chart showing portfolio peak-to-trough decline over time.
 * Uses red color intensity proportional to drawdown severity and
 * highlights the maximum drawdown period with a ReferenceArea.
 */
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceArea,
} from 'recharts';
import { formatDate } from '@/utils/formatters';
import type { DrawdownDataPoint } from '@/types/performance';

interface DrawdownChartProps {
  data: DrawdownDataPoint[];
  maxDrawdown: { value: number; startDate: string; endDate: string };
  isLoading?: boolean;
}

export function DrawdownChart({
  data,
  maxDrawdown,
  isLoading = false,
}: DrawdownChartProps) {
  if (isLoading) {
    return <div className="h-48 rounded shimmer" data-testid="chart-skeleton" />;
  }

  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-dark-700 bg-dark-800/50">
        <p className="text-sm text-dark-500">No drawdown data available</p>
      </div>
    );
  }

  return (
    <div>
      <div className="h-48" role="img" aria-label="Portfolio drawdown chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <defs>
              <linearGradient id="drawdownRedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.15} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatDate(value)}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={{ stroke: '#334155' }}
            />
            <YAxis
              tickFormatter={(value) => `${value.toFixed(1)}%`}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
              width={55}
              domain={['dataMin', 0]}
            />
            <Tooltip content={<DrawdownTooltip />} />
            {maxDrawdown.startDate && maxDrawdown.endDate && (
              <ReferenceArea
                x1={maxDrawdown.startDate}
                x2={maxDrawdown.endDate}
                fill="#f43f5e"
                fillOpacity={0.1}
                stroke="#f43f5e"
                strokeOpacity={0.3}
                strokeDasharray="3 3"
              />
            )}
            <Area
              type="monotone"
              dataKey="drawdown"
              stroke="#f43f5e"
              strokeWidth={2}
              fill="url(#drawdownRedGradient)"
              dot={false}
              activeDot={{ r: 4, fill: '#f43f5e', stroke: '#0f172a', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Accessible data table */}
      <table className="sr-only">
        <caption>Portfolio drawdown data</caption>
        <thead>
          <tr>
            <th>Date</th>
            <th>Drawdown %</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.date}>
              <td>{formatDate(point.date)}</td>
              <td>{point.drawdown.toFixed(2)}%</td>
              <td>{point.duration} days</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TooltipPayloadEntry {
  payload: DrawdownDataPoint;
}

interface DrawdownTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}

function DrawdownTooltip({ active, payload, label }: DrawdownTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const dataPoint = payload[0]!.payload;

  return (
    <div
      style={{
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        padding: '8px 12px',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
      }}
    >
      <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>
        {formatDate(label ?? dataPoint.date)}
      </p>
      <p style={{ color: '#f43f5e', fontSize: '13px', fontWeight: 500 }}>
        {dataPoint.drawdown.toFixed(2)}%
      </p>
      <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>
        Duration: {dataPoint.duration} days
      </p>
    </div>
  );
}
